import React from "react";
import { useHistory } from "react-router-dom";
import styles from "./CreatorMonetizationStep.module.css";
import heroImg from "../assets/creator-monetize-hero.png";
import monetizeOptionsProfileImg from "../assets/monetize-options-profile.png";
import monetizeOptionsProfileMobileImg from "../assets/monetize-options-profile-mobile.png";
import communityImg from "../../Landing/images/community.png";
import { Check } from "lucide-react";
import {
  CrownCardIcon,
  CoinsCardIcon,
  VideoCardIcon,
  LiveIcon,
  CreatorIcon,
  PhotosIcon,
  VideosIcon,
  PaidMessagingIcon,
  TipsDollarIcon,
  PaidPhotosIcon,
  PaidVideosIcon,
  ContentBundlesIcon,
  SocialLinkIcon,
  DiamondIcon,
  PrivateShowsBadgeIcon,
  LiveCamBadgeIcon,
  EyeBadgeIcon,
  LivePrivateShowIcon,
  LiveTipMenuIcon,
  LiveSexToyIcon,
  LiveShowRecordingsIcon,
  LiveDiamondIcon,
  StepCoinsIcon,
  StepPlayBadgeIcon,
  StepDollarCircleIcon,
  StepGiftIcon,
  FlowArrowUp,
  FlowArrowDown,
  RevenueCoinsIcon,
  RevenueUserIcon,
  RevenueCrownIcon,
  SplitTreeConnector,
  ThresholdCoinsIcon,
  ThresholdDollarIcon,
  ThresholdCheckIcon,
  ThresholdChevronRightIcon,
} from "../icons/CreatorMonetizationIcons";

const MONETIZATION_TILES = [
  { id: "messaging", title: "Paid\nMessaging", icon: <PaidMessagingIcon /> },
  { id: "tips", title: "Tips", icon: <TipsDollarIcon /> },
  { id: "photos", title: "Paid\nPhotos", icon: <PaidPhotosIcon /> },
  { id: "videos", title: "Paid\nVideos", icon: <PaidVideosIcon /> },
  { id: "bundles", title: "Content\nBundles", icon: <ContentBundlesIcon /> },
  { id: "social-unlocks", title: "Social Link\nUnlocks", icon: <SocialLinkIcon /> },
  { id: "custom-requests", title: "Custom\nRequests", icon: <DiamondIcon /> },
  { id: "private-shows", title: "Private\nshows", icon: <PrivateShowsBadgeIcon /> },
];

const LIVE_MONETIZATION_FEATURES = [
  {
    id: "private-shows",
    title: "Private Shows",
    description: "Set up private live experiences for individual members.",
    icon: <LivePrivateShowIcon />,
  },
  {
    id: "tip-menus",
    title: "Tip Menus",
    description: "Give members a simple way to support you during your live sessions.",
    icon: <LiveTipMenuIcon />,
  },
  {
    id: "sex-toy",
    title: "Sex Toy Interactions",
    description: "Offer approved interactive experiences with Credit-based pricing.",
    icon: <LiveSexToyIcon />,
  },
  {
    id: "show-recordings",
    title: "Show Recordings",
    description: "Make recordings of eligible shows available as paid content.",
    icon: <LiveShowRecordingsIcon />,
  },
  {
    id: "creator-subscriptions",
    title: "Creator Subscriptions",
    description: "Give subscribers access to additional creator-specific experiences.",
    icon: <LiveDiamondIcon />,
  },
];

interface CreatorMonetizationStepProps {
  onContinue: () => void;
  onSkip: () => void;
  onBack?: () => void;
}

export default function CreatorMonetizationStep({
  onContinue,
  onSkip,
}: CreatorMonetizationStepProps) {
  const history = useHistory();

  const handleSetupLater = () => {
    if (onSkip) {
      onSkip();
    } else {
      history.push("/");
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.wrapper}>
        {/* ── Top Hero Section ── */}
        <section className={styles.heroSection}>
          {/* Left Column: Heading & CTAs */}
          <div className={styles.heroContent}>
            <span className={styles.heroTag}>CREATOR MONETIZATION</span>
            <h1 className={styles.heroTitle}>
              <span>Turn your content into</span>
              <span>your earning.</span>
            </h1>
            <p className={styles.heroSubtitle}>
              Choose how you want to monetize your content, conversations, And live experiences.
              You control what you offer, how much you charge, and when its available.
            </p>

            <div className={styles.heroButtons}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={onContinue}
                id="continue-to-monetization-btn"
              >
                Continue To Monetization
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={handleSetupLater}
                id="setup-later-btn"
              >
                I'll Set This Up Later
              </button>
            </div>
          </div>

          {/* Right Column: Hero Visual with Glow Ring & Floating Badges */}
          <div className={styles.heroVisual}>
            <div className={styles.visualFrame}>
              {/* Ambient neon ring */}
              <div className={styles.neonRing} />

              {/* Cut-out portrait image */}
              <img
                src={heroImg}
                alt="Creator Monetization"
                className={styles.heroImage}
              />

              {/* Floating Badges */}
              <div className={`${styles.badge} ${styles.badgeLive}`}>
                <LiveIcon />
                <span>Live</span>
              </div>

              <div className={`${styles.badge} ${styles.badgeCredits}`}>
                <CreatorIcon />
                <span>1 Creator = $0.10</span>
              </div>

              <div className={`${styles.badge} ${styles.badgePhotos}`}>
                <PhotosIcon />
                <span>Photos</span>
              </div>

              <div className={`${styles.badge} ${styles.badgeVideos}`}>
                <VideosIcon />
                <span>Videos</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── Bottom Section: 3 Ways to Build ── */}
        <section className={styles.bottomSection}>
          <span className={styles.sectionTag}>HOW SUGARCRIBS MONETIZATION WORKS</span>
          <h2 className={styles.sectionTitle}>
            Three ways to Build your creator Business
          </h2>

          <div className={styles.cardsGrid}>
            {/* Card 1: Creator Subscriptions */}
            <div className={`${styles.card} ${styles.cardPink}`}>
              <div className={styles.cardHeader}>
                <div className={`${styles.cardIconBox} ${styles.iconBoxPink}`}>
                  <CrownCardIcon />
                </div>
                <div>
                  <h3 className={styles.cardTitle}>Creator Subscriptions</h3>
                  <div className={`${styles.cardSubtitle} ${styles.subtitlePink}`}>
                    Build a recurring audience.
                  </div>
                </div>
              </div>

              <p className={styles.cardDescription}>
                Offer subscribers exclusive access and define your own subscription price and benefits
              </p>

              <div className={styles.cardSectionHeading}>Your Control:</div>
              <ul className={styles.itemsList}>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCirclePink}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Subscription price</span>
                </li>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCirclePink}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Benefits</span>
                </li>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCirclePink}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Subscriber Experience</span>
                </li>
              </ul>
            </div>

            {/* Card 2: Credit Based Experiences */}
            <div className={`${styles.card} ${styles.cardAmber}`}>
              <div className={styles.cardHeader}>
                <div className={`${styles.cardIconBox} ${styles.iconBoxAmber}`}>
                  <CoinsCardIcon />
                </div>
                <div>
                  <h3 className={styles.cardTitle}>Credit Based Experiences</h3>
                  <div className={`${styles.cardSubtitle} ${styles.subtitleAmber}`}>
                    Get paid for premium interactions.
                  </div>
                </div>
              </div>

              <p className={styles.cardDescription}>
                Members use SugarCribs credits to unlock Experiences, Content, Conversations, and more.
              </p>

              <div className={styles.cardSectionHeading}>Credits can be used for:</div>
              <ul className={styles.itemsList}>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCircleAmber}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Paid messages</span>
                </li>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCircleAmber}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Tips</span>
                </li>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCircleAmber}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Photos &amp; Videos</span>
                </li>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCircleAmber}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Custom Requests</span>
                </li>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCircleAmber}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Private experiences</span>
                </li>
              </ul>
            </div>

            {/* Card 3: Live Cam & Private Experiences */}
            <div className={`${styles.card} ${styles.cardSlate}`}>
              <div className={styles.cardHeader}>
                <div className={`${styles.cardIconBox} ${styles.iconBoxSlate}`}>
                  <VideoCardIcon />
                </div>
                <div>
                  <h3 className={styles.cardTitle}>Live Cam &amp; Private Experiences</h3>
                  <div className={`${styles.cardSubtitle} ${styles.subtitleSlate}`}>
                    Turn Live interaction into earnings.
                  </div>
                </div>
              </div>

              <p className={styles.cardDescription}>
                Offer Live experiences where members can spend Credits to interact with you Directly.
              </p>

              <div className={styles.cardSectionHeading}>Monetize:</div>
              <ul className={styles.itemsList}>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCircleSlate}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Private Shows</span>
                </li>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCircleSlate}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Tip menus</span>
                </li>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCircleSlate}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Tips</span>
                </li>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCircleSlate}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Sex toy interactions</span>
                </li>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCircleSlate}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Show recordings</span>
                </li>
                <li className={styles.itemRow}>
                  <span className={`${styles.checkCircle} ${styles.checkCircleSlate}`}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className={styles.itemText}>Paid Content</span>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* ── Monetization Options & Live Cam Monetization Section ── */}
      <section className={styles.monetizationOptionsSection}>
        <div className={styles.monetizationOptionsInner}>
          {/* Left Column: Monetization Options */}
          <div className={styles.optionsColLeft}>
            <span className={styles.optionsTag}>YOUR CONTENT. YOUR RULES.</span>
            <h2 className={styles.optionsHeading}>Monetization Options</h2>
            <p className={styles.optionsSubtitle}>
              Choose the experiences you want to offer and decide how you want members to access them.
            </p>

            <div className={styles.tilesGrid}>
              {MONETIZATION_TILES.map((tile) => (
                <div key={tile.id} className={styles.tileCard}>
                  <div className={styles.tileIcon}>{tile.icon}</div>
                  <div className={styles.tileTitle}>{tile.title}</div>
                </div>
              ))}
            </div>

            <div className={styles.optionsFooterNote}>
              You can enable or disable monetization features at any time.
            </div>
          </div>

          {/* Center Column: Portrait Photo with Live & 2.4k Badges */}
          <div className={styles.optionsColCenter}>
            <div className={styles.profileCard}>
              <picture className={styles.profilePicture}>
                <source media="(max-width: 768px)" srcSet={monetizeOptionsProfileMobileImg} />
                <img
                  src={monetizeOptionsProfileImg}
                  alt="Monetization Options Profile"
                  className={styles.profileImg}
                />
              </picture>
              <div className={styles.profileBadges}>
                <div className={styles.badgeLivePill}>
                  <LiveCamBadgeIcon />
                  <span>Live</span>
                </div>
                <div className={styles.badgeViewersPill}>
                  <EyeBadgeIcon />
                  <span>2.4k</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Live Cam Monetization */}
          <div className={styles.optionsColRight}>
            <span className={styles.optionsTag}>LIVE CAM MONETIZATION</span>
            <h2 className={styles.optionsHeading}>
              Make Every Live<br />Experience Count
            </h2>
            <p className={styles.optionsSubtitle}>
              Offer members more personal, real-time experiences and monetize your time through flexible Credit-based options.
            </p>

            <div className={styles.featuresList}>
              {LIVE_MONETIZATION_FEATURES.map((item) => (
                <div key={item.id} className={styles.featureRow}>
                  <div className={styles.featureIconCircle}>
                    {item.icon}
                  </div>
                  <div className={styles.featureText}>
                    <div className={styles.featureTitle}>{item.title}</div>
                    <div className={styles.featureDesc}>{item.description}</div>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className={`${styles.primaryButton} ${styles.setUp}`}
              onClick={onContinue}
              id="continue-to-monetization-btn"
            >
              Set up Live Monetization
            </button>
          </div>
        </div>
      </section>

      {/* ── Understanding Credits & Revenue Split Section ── */}
      <section className={styles.creditsSection}>
        <div className={styles.creditsInner}>
          {/* Header */}
          <div className={styles.creditsHeader}>
            <span className={styles.creditsTag}>UNDERSTANDING CREDITS</span>
            <h2 className={styles.creditsTitle}>One Simple Currency. Multiple Ways to Earn</h2>
            <p className={styles.creditsSubtitle}>
              Credits are the universal currency on SugarCribs. They give members access to premium experiences and give creators a way to earn from their content and interactions.
            </p>
          </div>

          {/* 4 Step Process Flow */}
          <div className={styles.stepsFlow}>
            {/* Step 1 */}
            <div className={styles.stepItem}>
              <div className={styles.stepCircleWrap}>
                <div className={styles.stepBadge}>1</div>
                <div className={styles.stepCircle}>
                  <StepCoinsIcon />
                </div>
              </div>
              <h4 className={styles.stepTitle}>Member purchase credits</h4>
              <p className={styles.stepDesc}>
                Members buy credit packages or add credits to their wallets.
              </p>
            </div>

            {/* Connecting Arrow 1 -> 2 */}
            <div className={styles.arrowWrap}>
              <FlowArrowDown />
            </div>

            {/* Step 2 */}
            <div className={styles.stepItem}>
              <div className={styles.stepCircleWrap}>
                <div className={styles.stepBadge}>2</div>
                <div className={styles.stepCircle}>
                  <StepPlayBadgeIcon />
                </div>
              </div>
              <h4 className={styles.stepTitle}>Member spends credits</h4>
              <p className={styles.stepDesc}>
                Members Use their credits to book services, sessions, or purchases.
              </p>
            </div>

            {/* Connecting Arrow 2 -> 3 */}
            <div className={styles.arrowWrap}>
              <FlowArrowUp />
            </div>

            {/* Step 3 */}
            <div className={styles.stepItem}>
              <div className={styles.stepCircleWrap}>
                <div className={styles.stepBadge}>3</div>
                <div className={styles.stepCircle}>
                  <StepDollarCircleIcon />
                </div>
              </div>
              <h4 className={styles.stepTitle}>Creator receives credits</h4>
              <p className={styles.stepDesc}>
                Credits are added to the Creator's wallet (minus platform fee split).
              </p>
            </div>

            {/* Connecting Arrow 3 -> 4 */}
            <div className={styles.arrowWrap}>
              <FlowArrowDown />
            </div>

            {/* Step 4 */}
            <div className={styles.stepItem}>
              <div className={styles.stepCircleWrap}>
                <div className={styles.stepBadge}>4</div>
                <div className={styles.stepCircle}>
                  <StepGiftIcon />
                </div>
              </div>
              <h4 className={styles.stepTitle}>Creator withdraws earnings</h4>
              <p className={styles.stepDesc}>
                Creators convert earned credits to USD or other supported currencies (After reaching 200 credit minimu).
              </p>
            </div>
          </div>

          {/* Revenue Split Card */}
          <div className={styles.revenueSplitCard}>
            <div className={styles.revenueSplitLeft}>
              <span className={styles.revenueSplitTag}>REVENUE SPLIT</span>
              <h3 className={styles.revenueSplitTitle}>How the Credit Split Works</h3>
              <p className={styles.revenueSplitDesc}>
                SugarCribs empowers creators with complete autonomy over monetization features, custom pricing tiers, and real-time schedule availability.
              </p>
            </div>

            <div className={styles.revenueSplitRight}>
              <div className={styles.splitTreeDiagram}>
                {/* Top Node */}
                <div className={styles.splitNodeTop}>
                  <div className={styles.splitNodeIcon}>
                    <RevenueCoinsIcon />
                  </div>
                  <div className={styles.splitNodeText}>
                    <div className={styles.splitNodeCredits}>200 Credits</div>
                    <div className={styles.splitNodeVal}>$20 Transaction Value</div>
                  </div>
                </div>

                {/* Tree Connector */}
                <SplitTreeConnector className={styles.splitTreeConnector} />

                {/* Bottom Nodes */}
                <div className={styles.splitNodesBottomRow}>
                  {/* Left Node */}
                  <div className={styles.splitNodeBottom}>
                    <div className={styles.splitNodeIcon}>
                      <RevenueUserIcon />
                    </div>
                    <div className={styles.splitNodeText}>
                      <div className={styles.splitNodeCredits}>100 Credits</div>
                      <div className={styles.splitNodeVal}>Creator Earnings (50%)</div>
                    </div>
                  </div>

                  {/* Right Node */}
                  <div className={styles.splitNodeBottom}>
                    <div className={styles.splitNodeIcon}>
                      <RevenueCrownIcon />
                    </div>
                    <div className={styles.splitNodeText}>
                      <div className={styles.splitNodeCredits}>100 Credits</div>
                      <div className={styles.splitNodeVal}>SugarCribs (50%)</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Withdraw When You Reach 200 Credits ── */}
      <section className={styles.withdrawSection}>
        <div className={styles.withdrawInner}>
          <div className={styles.withdrawHeader}>
            <span className={styles.withdrawTag}>GET PAID</span>
            <h2 className={styles.withdrawTitle}>Withdraw When You Reach 200 Credits</h2>
            <p className={styles.withdrawSubtitle}>
              Once your wallet hits the minimum threshold, convert your Credits to USD or a supported currency.
            </p>
          </div>

          <div className={styles.withdrawCardsFlow}>
            {/* Card 1 */}
            <div className={styles.withdrawCard}>
              <div className={styles.withdrawIconWrap}>
                <ThresholdCoinsIcon />
              </div>
              <span className={styles.withdrawCardLabel}>Minimum Threshold</span>
              <h3 className={styles.withdrawCardValue}>200 Credits</h3>
              <p className={styles.withdrawCardDesc}>Earned through creator sales</p>
            </div>

            {/* Connector 1 -> 2 */}
            <div className={styles.withdrawArrowBadge}>
              <ThresholdChevronRightIcon />
            </div>

            {/* Card 2 */}
            <div className={styles.withdrawCard}>
              <div className={styles.withdrawIconWrap}>
                <ThresholdDollarIcon />
              </div>
              <span className={styles.withdrawCardLabel}>Gross Value (USD)</span>
              <h3 className={styles.withdrawCardValue}>$20 GTV</h3>
              <p className={styles.withdrawCardDesc}>Gross Transaction Value</p>
            </div>

            {/* Connector 2 -> 3 */}
            <div className={styles.withdrawArrowBadge}>
              <ThresholdChevronRightIcon />
            </div>

            {/* Card 3 */}
            <div className={styles.withdrawCard}>
              <div className={styles.withdrawIconWrap}>
                <ThresholdCheckIcon />
              </div>
              <span className={styles.withdrawCardLabel}>Status Unlocked</span>
              <h3 className={styles.withdrawCardValue}>Withdrawal Eligible</h3>
              <p className={styles.withdrawCardDesc}>Ready for instant payout request</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Community CTA Section ── */}
      <section className={styles.communitySection}>
        <div className={styles.communityCardBg}>
          <div className={styles.communityBlur} />
        </div>
        <div className={styles.communityText}>
          <h2 className={styles.communityTitle}>
            Ready to Set Up Your Monetization?
          </h2>
          <p className={styles.communitySubtitle}>
            Choose the monetization options that work for you. You can update your settings and pricing later.
          </p>
          <div className={styles.communityButtons}>
            <button
              type="button"
              className={styles.communityBtnPrimary}
              onClick={onContinue}
              id="community-setup-monetization-btn"
            >
              Set Up Monetization
            </button>
            <button
              type="button"
              className={styles.communityBtnLink}
              onClick={handleSetupLater}
              id="community-skip-monetization-btn"
            >
              Continue Without Monetization
            </button>
          </div>
          <div className={styles.communityNote}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={styles.communityNoteIcon}
            >
              <path
                d="M11.9995 8.5V14M11.9995 17.247V16.747M5.9795 10.762C8.6075 5.587 9.9195 3 11.9995 3C14.0795 3 15.3925 5.587 18.0195 10.762L18.3465 11.406C20.5285 15.706 21.6205 17.856 20.6335 19.428C19.6475 21 17.2075 21 12.3265 21H11.6725C6.7925 21 4.3515 21 3.3655 19.428C2.3795 17.856 3.4705 15.706 5.6525 11.406L5.9795 10.762Z"
                stroke="#131313"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>You can change any of these settings later.</span>
          </div>
        </div>
        <div className={styles.communityImageContainer}>
          <img
            src={communityImg}
            alt="SugarCribs Community"
            className={styles.communityImage}
          />
        </div>
      </section>
    </div>
  );
}
